import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Location } from '@ghostscreen/contracts';
import { LocationForm } from './LocationForm';

type EditLocationModalProps = {
  organisationId: string;
  location: Location;
  onClose: () => void;
  onSaved: () => void;
};

/** Edit name / timezone / address for an existing location. */
export function EditLocationModal({
  organisationId,
  location,
  onClose,
  onSaved,
}: EditLocationModalProps) {
  const { t } = useTranslation(['displays', 'common']);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-location-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'color-mix(in srgb, #0a0a0b 55%, transparent)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-[14px] border border-line p-5 shadow-lg sm:p-6"
        style={{ background: 'var(--gs-panel)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 id="edit-location-title" className="m-0 text-lg font-bold text-ink">
              {t('location.edit.title')}
            </h2>
            <p className="m-0 mt-1 text-sm text-muted">{t('location.edit.description')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-sm font-semibold text-muted hover:text-ink"
          >
            {t('common:actions.close')}
          </button>
        </div>

        <LocationForm
          key={location.id}
          organisationId={organisationId}
          location={location}
          onSaved={() => {
            onSaved();
            onClose();
          }}
        />
      </div>
    </div>,
    document.body,
  );
}
